import React from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Chip,
  Divider,
  IconButton,
  Link,
  Paper,
  Stack,
  Tooltip,
  Typography
} from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import LinkRoundedIcon from '@mui/icons-material/LinkRounded'
import PublicRoundedIcon from '@mui/icons-material/PublicRounded'
import ShareRoundedIcon from '@mui/icons-material/ShareRounded'
import ShareLinksDialog from '../common/ShareLinksDialog'
import { DateTime } from 'luxon'
import { useAuth } from '../../auth/AuthContext'
import LinkPopover from '../common/LinkPopover'
import LinkNotice, { copyWithNotice, useLinkNotice } from '../common/LinkNotice'
import env from '../../config/env'
import {
  PREVIEW_LINK_COPIED,
  PREVIEW_LINK_FAILED,
  PREVIEW_LINK_LABEL,
  PREVIEW_LINK_NOT_COPIED,
  PREVIEW_LINK_NOTICE_MS,
  PREVIEW_LINK_TOOLTIP,
  SIGNED_IN_LINK_NOT_COPIED,
  WATCH_LINKS_LABEL
} from '../../constants/sharing'
import { createPublicLink } from '../../service'
import { uploadStatusLabel } from '../upload/UploadProcessingStatus'

UploadCard.propTypes = {
  upload: PropTypes.object,
  onEdit: PropTypes.func,
  embedded: PropTypes.bool,
  lockedReason: PropTypes.string
}

const sourceTypeLabel = value => {
  if (value === 'game_part') return 'Game part'
  if (value === 'shift_clip') return 'Shift clip'
  if (value === 'highlight_clip') return 'Highlight'
  return 'Whole game'
}

// lockedReason: why no new link can be made here (a part combined into a full game video, D34), or null.
export default function UploadCard ({ upload, onEdit, embedded = false, lockedReason = null }) {
  const { user, groups: userGroups } = useAuth()
  const { anchor: popoverEl, notice, show, close } = useLinkNotice()
  const [linksOpen, setLinksOpen] = React.useState(false)
  const isOwner = Boolean(user && user.username === upload.owner)
  const canEdit = isOwner || userGroups.some(group => (upload.groups || []).includes(group))

  // React clears ev.currentTarget once the handler yields, so capture it before awaiting.
  const copyNormalLink = ev => copyWithNotice({
    url: `${env.publicLinkUrl}/upload/${upload.id}`,
    anchor: ev.currentTarget,
    show,
    copied: 'Upload link copied. Login is still required.',
    notCopied: SIGNED_IN_LINK_NOT_COPIED,
    durationMs: 2500
  })

  const copyPublicLink = async ev => {
    const anchor = ev.currentTarget
    let link
    try {
      link = await createPublicLink({ uploadId: upload.id })
    } catch (err) {
      console.error(err)
      // 409: this part was combined into a full game video meanwhile; the API says so (D34).
      const message = err?.status === 409 && err.detail ? err.detail : PREVIEW_LINK_FAILED
      show(anchor, { message, severity: 'error' }, { durationMs: PREVIEW_LINK_NOTICE_MS })
      return
    }
    // The link now exists. If copying fails, show it rather than inviting a retry
    // that would create another one.
    await copyWithNotice({
      url: `${env.publicLinkUrl}${link.path}`,
      anchor,
      show,
      copied: PREVIEW_LINK_COPIED,
      notCopied: PREVIEW_LINK_NOT_COPIED,
      durationMs: PREVIEW_LINK_NOTICE_MS
    })
  }

  return (
    <Paper
      variant="outlined"
      className={embedded ? undefined : 'editor-inspector-panel'}
      sx={{
        overflow: 'hidden',
        border: embedded ? 0 : undefined,
        borderRadius: embedded ? 0 : undefined,
        background: embedded ? 'transparent' : undefined,
        boxShadow: embedded ? 'none' : undefined
      }}
    >
      <Box sx={{ px: 1.5, pt: 1.1, pb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          {!embedded
            ? <Typography
                variant="caption"
                color="primary.light"
                className="editor-kicker"
                sx={{ fontSize: 13, fontWeight: 750 }}
              >
                Source
              </Typography>
            : <Box />}

          <Stack direction="row" spacing={0.15} flexShrink={0}>
            <Tooltip title="Edit source details">
              <span>
                <IconButton
                  size="small"
                  onClick={ev => onEdit && onEdit(ev, upload)}
                  disabled={!canEdit}
                >
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Copy authenticated link">
              <IconButton size="small" onClick={copyNormalLink}>
                <LinkRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={lockedReason || PREVIEW_LINK_TOOLTIP} describeChild>
              <span>
                <IconButton
                  size="small"
                  aria-label={PREVIEW_LINK_LABEL}
                  onClick={copyPublicLink}
                  disabled={!canEdit || Boolean(lockedReason)}
                >
                  <PublicRoundedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            {isOwner
              ? (
                <Tooltip title={WATCH_LINKS_LABEL}>
                  <IconButton size="small" aria-label="Shared links" onClick={() => setLinksOpen(true)}>
                    <ShareRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                )
              : null}
          </Stack>
        </Stack>

        <Tooltip title={upload.name} placement="top-start">
          <Typography
            variant="subtitle2"
            noWrap
            sx={{
              mt: embedded ? 0 : 0.35,
              fontWeight: 750,
              lineHeight: 1.3,
              width: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {upload.name}
          </Typography>
        </Tooltip>
      </Box>

      <Divider />

      <Stack spacing={1} sx={{ px: 1.5, py: 1.25 }}>
        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
          <Chip
            size="small"
            label={sourceTypeLabel(upload.sourceType)}
            variant="outlined"
            sx={{ height: 23 }}
          />
          {upload.sequenceIndex
            ? <Chip
                size="small"
                label={`#${upload.sequenceIndex}`}
                variant="outlined"
                sx={{ height: 23 }}
              />
            : null}
          <Chip
            size="small"
            label={uploadStatusLabel(upload)}
            color={upload.readyForPlayback ? 'success' : upload.status === 'failed' ? 'error' : 'default'}
            variant={upload.readyForPlayback ? 'filled' : 'outlined'}
            sx={{ height: 23 }}
          />
        </Stack>

        {upload.description
          ? <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                lineHeight: 1.45,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}
            >
              {upload.description}
            </Typography>
          : null}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gap: '3px 10px',
            '& > .MuiTypography-root': { fontSize: 13 },
            '& > .label': { color: 'text.secondary' }
          }}
        >
          {upload.date
            ? <>
                <Typography variant="caption" className="label">Date</Typography>
                <Typography variant="caption">
                  {DateTime.fromISO(upload.date).toLocaleString(DateTime.DATETIME_MED)}
                </Typography>
              </>
            : null}
          {upload.owner
            ? <>
                <Typography variant="caption" className="label">Owner</Typography>
                <Typography variant="caption" noWrap>{upload.owner}</Typography>
              </>
            : null}
          <>
            <Typography variant="caption" className="label">Access</Typography>
            <Typography variant="caption">
              {upload.groups && upload.groups.length
                ? upload.groups.join(', ')
                : 'Private'}
            </Typography>
          </>
        </Box>

        {/* D34 release B: the originals stay reachable, with their AI reports, through ?original=1. */}
        {upload.combinedFrom?.length
          ? <Box component="section" aria-label="Combined from">
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 13 }}>
                {`Combined from ${upload.combinedFrom.length} parts`}
              </Typography>
              <Box component="ol" sx={{ m: 0, mt: 0.25, pl: 2.5, '& li': { fontSize: 13 } }}>
                {upload.combinedFrom.map(part => (
                  <li key={part.uploadId}>
                    <Link component={RouterLink} to={`/upload/${part.uploadId}?original=1`} variant="caption" sx={{ fontSize: 13 }}>
                      {part.name}
                    </Link>
                  </li>
                ))}
              </Box>
            </Box>
          : null}
      </Stack>

      <LinkPopover el={popoverEl} setEl={close} horizontal="left">
        <LinkNotice notice={notice} />
      </LinkPopover>
      {isOwner
        ? <ShareLinksDialog open={linksOpen} onClose={() => setLinksOpen(false)} uploadId={upload.id} title={upload.name} combinedFrom={upload.combinedFrom} lockedReason={lockedReason} />
        : null}
    </Paper>
  )
}
